import React from 'react';
import {API} from '@chekin/api';
import {IS_DEV_MODE} from '../../../utils/devMode';
import type {WebsocketContextProps} from './WebsocketContext';

const RECONNECT_TIMEOUT_S = 0.5;

export function useWebsocketConnection(enabled: boolean): WebsocketContextProps {
  const ws = React.useRef<WebSocket>();
  const [message, setMessage] = React.useState<{event_type: null} | string>('');
  const reconnectRef = React.useRef<ReturnType<typeof setTimeout>>();
  const shouldReconnectRef = React.useRef(true);

  const clearMessage = React.useCallback(() => {
    setMessage('');
  }, []);

  const cleanup = React.useCallback(() => {
    shouldReconnectRef.current = false;
    clearTimeout(reconnectRef.current);
    ws?.current?.close();
  }, []);

  const handleWebSocketOpen = React.useCallback(() => {
    clearTimeout(reconnectRef.current);

    if (IS_DEV_MODE) {
      console.log('%c [WS opened]', 'color: #35E5BC');
    }
  }, []);

  const handleWebSocketError = React.useCallback(() => {
    const isWebSocketClosed = ws?.current?.readyState !== WebSocket.OPEN;

    if (isWebSocketClosed) {
      return;
    }

    ws.current?.close();
  }, []);

  const handleWebSocketMessage = React.useCallback((event: MessageEvent) => {
    const message = JSON.parse(event.data);
    if (message.pong) return;
    setMessage(message);
  }, []);

  const handleWebSocketClose = React.useCallback(
    (event: CloseEvent, reconnectionCallback: () => void) => {
      const canReconnect = shouldReconnectRef.current && enabled;

      if (canReconnect) {
        reconnectRef.current = setTimeout(() => {
          reconnectionCallback();
        }, RECONNECT_TIMEOUT_S * 1000);

        if (IS_DEV_MODE) {
          console.log(
            `%c [WS closed] Reconnecting in ${RECONNECT_TIMEOUT_S} seconds.`,
            'color: #FF2467',
          );
        }
        return;
      }

      if (IS_DEV_MODE) {
        console.log(`%c [WS closed]`, 'color: #FF2467');
      }
    },
    [enabled],
  );

  const connect = React.useCallback(() => {
    ws.current = API.webSocket.connectWebSocket();
    shouldReconnectRef.current = true;

    ws.current.onopen = handleWebSocketOpen;
    ws.current.onerror = handleWebSocketError;
    ws.current.onmessage = handleWebSocketMessage;
    ws.current.onclose = event => {
      handleWebSocketClose(event, connect);
    };
  }, [
    handleWebSocketClose,
    handleWebSocketError,
    handleWebSocketMessage,
    handleWebSocketOpen,
  ]);

  React.useEffect(
    function setupConnection() {
      const reconnect = () => {
        const isWebSocketClosed = ws?.current?.readyState !== WebSocket.OPEN;
        if (isWebSocketClosed) {
          cleanup();
          connect();
        }
      };

      if (!enabled) {
        cleanup();
      } else {
        connect();
        window.addEventListener('focus', reconnect);
      }

      return () => {
        cleanup();
        window.removeEventListener('focus', reconnect);
      };
    },
    [cleanup, connect, enabled],
  );

  return React.useMemo<WebsocketContextProps>(
    () => ({message, clearMessage, send: ws?.current?.send, cleanup, connect}),
    [cleanup, clearMessage, connect, message],
  );
}
